import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Clock, Globe, Send, Loader2, BookOpen } from "lucide-react";
import { useSite } from "../components/SiteLayout";
import { sendEnquiry } from "../lib/site";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";

export default function SiteContact() {
  const site = useSite();
  const [params] = useSearchParams();
  const courseName = params.get("course") || "";
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (courseName) {
      setForm((f) => ({
        ...f,
        subject: f.subject || `Course enquiry: ${courseName}`,
        message: f.message || `I am interested in admission to ${courseName}. Please share more details.`,
      }));
    }
  }, [courseName]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.message.trim()) {
      toast.error("Please enter your name and a message.");
      return;
    }
    setSending(true);
    try {
      await sendEnquiry({ ...form, course: courseName });
      toast.success("Thank you! Your enquiry has been submitted.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch {
      toast.error("Could not submit your enquiry. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12" data-testid="site-contact">
      <div className="text-xs uppercase tracking-widest text-blue-800 font-semibold mb-1">Contact Us</div>
      <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 mb-8">Get in Touch</h1>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.2fr] gap-8">
        {/* info */}
        <div className="rounded-xl border border-slate-200 bg-white p-6 space-y-4 h-fit" data-testid="contact-info">
          {site.address && (
            <div className="flex items-start gap-3 text-sm text-slate-600">
              <MapPin size={17} className="text-blue-800 shrink-0 mt-0.5" />
              <span>{site.address}</span>
            </div>
          )}
          {site.phone && (
            <div className="flex items-start gap-3 text-sm text-slate-600">
              <Phone size={17} className="text-blue-800 shrink-0 mt-0.5" />
              <span>{site.phone}</span>
            </div>
          )}
          {site.phone2 && (
            <div className="flex items-start gap-3 text-sm text-slate-600">
              <Phone size={17} className="text-blue-800 shrink-0 mt-0.5" />
              <span>{site.phone2}</span>
            </div>
          )}
          {site.email && (
            <div className="flex items-start gap-3 text-sm text-slate-600">
              <Mail size={17} className="text-blue-800 shrink-0 mt-0.5" />
              <span>{site.email}</span>
            </div>
          )}
          {site.office_hours && (
            <div className="flex items-start gap-3 text-sm text-slate-600">
              <Clock size={17} className="text-blue-800 shrink-0 mt-0.5" />
              <span>{site.office_hours}</span>
            </div>
          )}
          {site.website && (
            <div className="flex items-start gap-3 text-sm text-slate-600">
              <Globe size={17} className="text-blue-800 shrink-0 mt-0.5" />
              <span>{site.website}</span>
            </div>
          )}
        </div>

        {/* form */}
        <div>
          {courseName && (
            <div className="mb-4 rounded-lg bg-blue-50 border border-blue-100 px-4 py-3 flex items-center gap-2.5 text-sm text-blue-900"
              data-testid="contact-course-banner">
              <BookOpen size={15} className="shrink-0" />
              Enquiring about: <b>{courseName}</b>
            </div>
          )}
          <form onSubmit={submit} className="rounded-xl border border-slate-200 bg-white p-6 space-y-4" data-testid="contact-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="cname">Name *</Label>
              <Input id="cname" data-testid="contact-name" value={form.name} className="mt-1.5 h-10"
                onChange={(e) => set("name", e.target.value)} />
            </div>
            <div>
              <Label htmlFor="cemail">Email</Label>
              <Input id="cemail" type="email" data-testid="contact-email" value={form.email} className="mt-1.5 h-10"
                onChange={(e) => set("email", e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="cphone">Phone</Label>
              <Input id="cphone" data-testid="contact-phone" value={form.phone} className="mt-1.5 h-10"
                onChange={(e) => set("phone", e.target.value)} />
            </div>
            <div>
              <Label htmlFor="csubject">Subject</Label>
              <Input id="csubject" data-testid="contact-subject" value={form.subject} className="mt-1.5 h-10"
                onChange={(e) => set("subject", e.target.value)} />
            </div>
          </div>
          <div>
            <Label htmlFor="cmessage">Message *</Label>
            <Textarea id="cmessage" data-testid="contact-message" value={form.message} rows={5} className="mt-1.5"
              onChange={(e) => set("message", e.target.value)} />
          </div>
          <Button type="submit" data-testid="contact-submit" disabled={sending}
            className="bg-blue-800 hover:bg-blue-900 h-10 px-6">
            {sending ? <Loader2 size={15} className="animate-spin mr-2" /> : <Send size={15} className="mr-2" />}
            Send Message
          </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
